'use client'

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ethers, ZeroHash } from 'ethers';
import styles from '../styles/PurchaseFlow.module.css';
import Image from 'next/image';
import { useAccount } from 'wagmi';
import { sendTransaction, switchChain } from 'wagmi/actions';
import { networks, wagmiAdapter, ETHEREUM_CHAIN_ID, POLYGON_CHAIN_ID, BASE_CHAIN_ID } from '../../config';
import { parseEther, toHex } from 'viem';
import { AppProvider } from '../../context';
import { createAppKit } from '@reown/appkit' 
import SaftAgreementModal from './SaftAgreementModal'; // Import the SAFT modal

// Debug helper function to generate a unique ID for this component instance
const DEBUG_ID = `ps_${Math.random().toString(36).substr(2, 5)}`;
console.log(`[DEBUG ${DEBUG_ID}] PurchaseStep module loaded`);

// Update CryptoCurrency type to include SOL and ETH_BASE
type CryptoCurrency = 'ETH' | 'USDT' | 'USDC' | 'MATIC' | 'SOL' | 'ETH_BASE' | 'USDC_BASE';

interface PurchaseStepProps {
  selectedCrypto: CryptoCurrency; // Accept selected crypto as prop
  referralCode: string | null; // Add referral code prop
  onPrevious: () => void;
  onNext: (transactionHash: string, tokenAmount: number) => void;
  userEmail?: string | null; // Add optional userEmail prop
}

// Interface only for static data
interface CryptoStaticInfo {
  name: string;
  contractAddress?: string;
  symbolForApi: string; // Symbol used for CoinGecko API (lowercase)
  iconUrl: string;
  color: string;
}

// Static data for cryptos (no prices here)
const cryptoStaticData: Record<CryptoCurrency, CryptoStaticInfo> = {
  'ETH': { 
    name: 'Ethereum', 
    symbolForApi: 'ethereum',
    iconUrl: '/images/crypto/eth.svg', 
    color: '#627EEA'
  },
  'USDT': {
    name: 'Tether',
    contractAddress: process.env.NEXT_PUBLIC_USDT_CONTRACT_ADDRESS as string,
    symbolForApi: 'tether',
    iconUrl: '/images/crypto/usdt.svg',
    color: '#26A17B'
  },
  'USDC': {
    name: 'USD Coin',
    contractAddress: process.env.NEXT_PUBLIC_USDC_CONTRACT_ADDRESS as string,
    symbolForApi: 'usd-coin',
    iconUrl: '/images/crypto/usdc.svg',
    color: '#2775CA'
  },
  'MATIC': {
    name: 'Polygon',
    symbolForApi: 'matic-network',
    iconUrl: '/images/crypto/matic.svg',
    color: '#8247E5'
  },
  'SOL': {
    name: 'Solana',
    symbolForApi: 'solana',
    iconUrl: '/images/crypto/sol.svg',
    color: '#14F195'
  },
  'ETH_BASE': { // Add Base ETH
    name: 'Ethereum (Base)',
    symbolForApi: 'ethereum', // Base uses ETH, price is the same
    iconUrl: '/images/crypto/base.svg', // Assuming you have a base icon
    color: '#0052FF' // Base official blue color
  },
  'USDC_BASE': { // Add Base USDC
    name: 'USD Coin (Base)',
    contractAddress: process.env.NEXT_PUBLIC_BASE_USDC_CONTRACT_ADDRESS as string,
    symbolForApi: 'usd-coin', // USDC price is the same regardless of network
    iconUrl: '/images/crypto/usdc.svg',
    color: '#2775CA'
  }
};

// Default fallback prices (only used if API fails for selected crypto)
const defaultPrices: Record<CryptoCurrency, number> = {
  'ETH': 3500,
  'USDT': 1,
  'USDC': 1,
  'MATIC': 0.5,
  'SOL': 150,
  'ETH_BASE': 3500, // Same as ETH
  'USDC_BASE': 1  // Same as USDC
};

// Add Polygon treasury address
const TREASURY_ADDRESS = process.env.NEXT_PUBLIC_TREASURY_WALLET;
const POLYGON_TREASURY_ADDRESS = process.env.NEXT_PUBLIC_POLYGON_TREASURY_WALLET;
const SOLANA_TREASURY_ADDRESS = process.env.NEXT_PUBLIC_SOLANA_TREASURY_WALLET;
const BASE_TREASURY_ADDRESS = process.env.NEXT_PUBLIC_BASE_TREASURY_WALLET; // Add Base treasury wallet
const TOKEN_PRICE = 0.012; // USD per token

// First, let's declare the global functions in TypeScript at the top of the file
declare global {
  interface Window {
    confirmSolanaPayment?: (txHash: string) => void;
    cancelSolanaPayment?: () => void;
  }
}

// Declare the component logic first
const PurchaseStepComponent = ({ selectedCrypto, referralCode, onPrevious, onNext, userEmail }: PurchaseStepProps) => {
  console.log(`[DEBUG ${DEBUG_ID}] PurchaseStep render started for ${selectedCrypto}`);
  const renderCount = useRef(0);
  renderCount.current++;

  // Get wallet state directly from wagmi
  const { address, isConnected, chainId } = useAccount();

  // State
  const [emailAddress, setEmailAddress] = useState<string | null>(userEmail || null); // Initialize with userEmail if provided
  const [amount, setAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputMode, setInputMode] = useState<'crypto' | 'gam' | 'usd'>('crypto');
  const [selectedCryptoPrice, setSelectedCryptoPrice] = useState<number | null>(null); // Price for ONLY the selected crypto
  const [loadingPrice, setLoadingPrice] = useState<boolean>(true); // Start loading true
  const [requiredNetworkId, setRequiredNetworkId] = useState<number | null>(null); // State for required network
  const [showApprovalWarning, setShowApprovalWarning] = useState<boolean>(true); // State for approval warning visibility
  const [isSaftModalOpen, setIsSaftModalOpen] = useState(false); // State for SAFT modal

  // Effect to get stored email (can remain)
  useEffect(() => {
    // If userEmail prop is provided, use it immediately
    if (userEmail) {
      setEmailAddress(userEmail);
      console.log(`[DEBUG ${DEBUG_ID}] Using provided email: ${userEmail}`);
      return;
    }
    
    // Otherwise, try to get from localStorage
    const storedEmail = localStorage.getItem('gam_verified_email');
    if (storedEmail) {
      setEmailAddress(storedEmail);
      console.log(`[DEBUG ${DEBUG_ID}] Got stored email: ${storedEmail}`);
    }
  }, [userEmail]);

  // Fetch price ONLY for the selected cryptocurrency prop when it's provided or changes
  useEffect(() => {
    // Ensure we have a selected crypto to fetch for
    if (!selectedCrypto) {
        console.warn(`[DEBUG ${DEBUG_ID}] Price fetch skipped: No selected crypto.`);
        setLoadingPrice(false); // Stop loading if no crypto selected
        return; 
    }

    const fetchSelectedPrice = async () => {
      console.log(`[DEBUG ${DEBUG_ID}] Fetching price for selected crypto prop: ${selectedCrypto}`);
      setLoadingPrice(true);
      setSelectedCryptoPrice(null); // Reset price while fetching
      setError(null); // Clear previous errors

      try {
        const apiSymbol = cryptoStaticData[selectedCrypto].symbolForApi;
        const response = await fetch(
          `https://api.coingecko.com/api/v3/simple/price?ids=${apiSymbol}&vs_currencies=usd`
        );

        if (!response.ok) {
          throw new Error(`API Error: ${response.statusText} (${response.status})`);
        }

        const data = await response.json();

        if (data[apiSymbol]?.usd) {
          setSelectedCryptoPrice(data[apiSymbol].usd);
          console.log(`[DEBUG ${DEBUG_ID}] Successfully fetched price for ${selectedCrypto}: $${data[apiSymbol].usd}`);
        } else {
          console.warn(`[DEBUG ${DEBUG_ID}] Price not found in API response for ${apiSymbol}. Falling back.`);
          setSelectedCryptoPrice(defaultPrices[selectedCrypto]); // Use fallback price if key missing
          setError(`Could not fetch live price for ${selectedCrypto}. Using estimate.`);
        }
      } catch (err: any) {
        console.error(`[DEBUG ${DEBUG_ID}] Error fetching price for ${selectedCrypto}:`, err.message);
        console.log(`[DEBUG ${DEBUG_ID}] Falling back to default price for ${selectedCrypto}`);
        setSelectedCryptoPrice(defaultPrices[selectedCrypto]); // Use fallback price on error
        setError(`Could not fetch live price for ${selectedCrypto}. Using estimate.`); // Inform user about fallback
      } finally {
        setLoadingPrice(false);
      }
    };

    fetchSelectedPrice();

  }, [selectedCrypto]); // Re-run ONLY when the selectedCrypto prop changes

  // --- Calculations (Memoized) ---
  const tokenToCryptoRate = useMemo(() => {
    if (!selectedCryptoPrice) return 0;
    console.log(`[DEBUG ${DEBUG_ID}] Calculating tokenToCryptoRate`);
    return selectedCryptoPrice > 0 ? TOKEN_PRICE / selectedCryptoPrice : 0;
  }, [selectedCryptoPrice]);

  const estimatedTokens = useMemo(() => {
    if (!selectedCryptoPrice || !amount) return 0;
    console.log(`[DEBUG ${DEBUG_ID}] Calculating estimatedTokens`);
    const priceRatio = selectedCryptoPrice > 0 ? selectedCryptoPrice / TOKEN_PRICE : 0;
    if (inputMode === 'crypto') {
      return parseFloat(amount) * priceRatio;
    } else if (inputMode === 'usd') {
      return parseFloat(amount) / TOKEN_PRICE;
    } else {
      return parseFloat(amount) || 0;
    }
  }, [inputMode, amount, selectedCryptoPrice]);

  const estimatedCrypto = useMemo(() => {
    if (!tokenToCryptoRate || !amount) return 0;
    console.log(`[DEBUG ${DEBUG_ID}] Calculating estimatedCrypto`);
    if (inputMode === 'gam') {
      return parseFloat(amount) * tokenToCryptoRate;
    } else if (inputMode === 'usd') {
      return selectedCryptoPrice ? parseFloat(amount) / selectedCryptoPrice : 0;
    } else {
      return parseFloat(amount) || 0;
    }
  }, [inputMode, amount, tokenToCryptoRate, selectedCryptoPrice]);

  const estimatedUsd = useMemo(() => {
    if (!amount) return 0;
    console.log(`[DEBUG ${DEBUG_ID}] Calculating estimatedUsd`);
    if (inputMode === 'usd') {
      return parseFloat(amount) || 0;
    } else if (inputMode === 'crypto') {
      return selectedCryptoPrice ? parseFloat(amount) * selectedCryptoPrice : 0;
    } else { // gam mode
      return parseFloat(amount) * TOKEN_PRICE;
    }
  }, [inputMode, amount, selectedCryptoPrice]);

  // --- Handlers ---
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    console.log(`[DEBUG ${DEBUG_ID}] Amount input changed: ${value}`);
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount(value);
      // Clear error related to amount input, but keep fetch error if present
      if (error && error.includes('amount')) {
        setError(null);
      } else if (!error?.includes('live price')) {
          setError(null); // Clear other errors too unless it's the price fallback message
      }
    }
  };

  // Function to handle network switching
  const handleSwitchNetwork = useCallback(async () => {
    if (!requiredNetworkId) return;
    console.log(`[DEBUG ${DEBUG_ID}] Attempting to switch network to ID: ${requiredNetworkId}`);
    setProcessing(true);
    setError(null); // Clear previous error
    try {
      await switchChain(wagmiAdapter.wagmiConfig, { chainId: requiredNetworkId });
      console.log(`[DEBUG ${DEBUG_ID}] Network switch successful (or already on correct network)`);
      setRequiredNetworkId(null); // Clear required network state after successful switch
    } catch (switchError: any) {
      console.error(`[DEBUG ${DEBUG_ID}] Error switching network:`, switchError);
      // Sanitize switch error message more thoroughly
      let switchErrorMessage = 'Unknown error';
      if (switchError.shortMessage || switchError.message) {
        switchErrorMessage = (switchError.shortMessage || switchError.message)
                              .replace(/&/g, '&amp;') // Must be first
                              .replace(/</g, '&lt;')
                              .replace(/>/g, '&gt;')
                              .replace(/"/g, '&quot;');
      }
      setError(`Failed to switch network. Please try switching manually in your wallet. Error: ${switchErrorMessage}`);
    } finally {
      setProcessing(false);
    }
  }, [requiredNetworkId]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log(`[DEBUG ${DEBUG_ID}] Form submission started for ${selectedCrypto}`);
    const submitStart = performance.now();
    
    // Check if we have an email - if not, try to get it from localStorage again
    if (!emailAddress) {
      const storedEmail = localStorage.getItem('gam_verified_email');
      if (storedEmail) {
        setEmailAddress(storedEmail);
        console.log(`[DEBUG ${DEBUG_ID}] Retrieved email from localStorage: ${storedEmail}`);
      } else {
        setError('Email address not found. Please go back and verify your email.');
        console.log(`[DEBUG ${DEBUG_ID}] Submit error: No email address found`);
        return;
      }
    }
    
    let cryptoAmountToPay = 0;
    let tokensToReceive = 0;
    let usdAmount = 0;

    if (inputMode === 'crypto') {
      cryptoAmountToPay = parseFloat(amount);
      tokensToReceive = estimatedTokens;
      usdAmount = estimatedUsd;
    } else if (inputMode === 'gam') {
      cryptoAmountToPay = estimatedCrypto;
      tokensToReceive = parseFloat(amount);
      usdAmount = parseFloat(amount) * TOKEN_PRICE;
    } else { // usd mode
      cryptoAmountToPay = estimatedCrypto;
      tokensToReceive = estimatedTokens;
      usdAmount = parseFloat(amount); // Use exact USD amount entered by user
    }

    if (!cryptoAmountToPay || cryptoAmountToPay <= 0 || !tokensToReceive || tokensToReceive <= 0) {
      setError('Please enter a valid amount');
      console.log(`[DEBUG ${DEBUG_ID}] Submit error: Invalid amount calculations`);
      return;
    }

    // Log values to help with debugging
    console.log(`[DEBUG ${DEBUG_ID}] Purchase values:`, {
      inputMode,
      enteredAmount: parseFloat(amount),
      cryptoAmountToPay,
      tokensToReceive,
      usdAmount,
      estimatedUsd
    });

    if (!isConnected || !address) {
      setError('Wallet not connected. Please connect using the button at the top.');
      console.log(`[DEBUG ${DEBUG_ID}] Submit error: No wallet connected according to useAccount. isConnected: ${isConnected}, address: ${address}`);
      return;
    }

    if (!selectedCryptoPrice) {
        setError('Price data not available yet. Please wait or go back.');
        console.log(`[DEBUG ${DEBUG_ID}] Submit error: Price not loaded`);
        return;
    }
    
    try {
      setProcessing(true);
      setRequiredNetworkId(null); // Reset required network on new submit attempt
      if (!error?.includes('live price')) setError(null);
      console.log(`[DEBUG ${DEBUG_ID}] Processing payment: ${cryptoAmountToPay} ${selectedCrypto} for ${tokensToReceive} GAM (${usdAmount} USD)`);
      
      let transactionHash: `0x${string}` | string = '';
      
      // Handle different payment methods
      if (selectedCrypto === 'ETH') {
      console.log(`[DEBUG ${DEBUG_ID}] Preparing ETH transaction...`);
        
        // Verify user is on Ethereum network
        if (chainId !== ETHEREUM_CHAIN_ID) {
          setError('Incorrect Network: Please switch to the Ethereum network to complete this transaction.');
          setRequiredNetworkId(ETHEREUM_CHAIN_ID); // Set required network ID
          console.log(`[DEBUG ${DEBUG_ID}] Network mismatch for ${selectedCrypto}. Current: ${chainId}, Expected: ${ETHEREUM_CHAIN_ID}`);
          setProcessing(false);
          return;
        }
        
        let gasParams = {};
        
        // Create transaction object
        const tx: any = {
          chainId: ETHEREUM_CHAIN_ID,
          to: TREASURY_ADDRESS as `0x${string}`,
          value: parseEther(cryptoAmountToPay.toString()),
          data: toHex(ethers.toUtf8Bytes(`GAM Pre-Mint: ${tokensToReceive.toFixed(2)} tokens`)),
          gasLimit: 50000, 
        };

      console.log(`[DEBUG ${DEBUG_ID}] Sending ETH tx via wagmi action:`, tx);
        transactionHash = await sendTransaction(wagmiAdapter.wagmiConfig, tx);
      } else if (selectedCrypto === 'MATIC') {
        console.log(`[DEBUG ${DEBUG_ID}] Preparing MATIC transaction...`);
        const polygonTreasuryAddress = POLYGON_TREASURY_ADDRESS || TREASURY_ADDRESS;
        
        // Verify chain ID
        if (chainId !== POLYGON_CHAIN_ID) {
          setError('Incorrect Network: Please switch to the Polygon network to complete this transaction.');
          setRequiredNetworkId(POLYGON_CHAIN_ID); // Set required network ID
          console.log(`[DEBUG ${DEBUG_ID}] Network mismatch for ${selectedCrypto}. Current: ${chainId}, Expected: ${POLYGON_CHAIN_ID}`);
          setProcessing(false);
          return;
        }
        
        let gasParams = {};
        
        // Create transaction object
        const tx: any = {
          chainId: POLYGON_CHAIN_ID,
          to: polygonTreasuryAddress as `0x${string}`,
          value: parseEther(cryptoAmountToPay.toString()),
          data: toHex(ethers.toUtf8Bytes(`GAM Pre-Mint: ${tokensToReceive.toFixed(2)} tokens on Polygon`)),
          gasLimit: 50000, 
        };

        console.log(`[DEBUG ${DEBUG_ID}] Sending MATIC tx via wagmi action to ${polygonTreasuryAddress}:`, tx);
        transactionHash = await sendTransaction(wagmiAdapter.wagmiConfig, tx);
      } else if (selectedCrypto === 'ETH_BASE') { // Add Case for ETH_BASE
        console.log(`[DEBUG ${DEBUG_ID}] Preparing ETH_BASE transaction...`);
        const baseTreasuryAddress = BASE_TREASURY_ADDRESS || TREASURY_ADDRESS; // Use Base specific or fallback

        // Verify chain ID
        if (chainId !== BASE_CHAIN_ID) {
          setError('Incorrect Network: Please switch to the Base network to complete this transaction.');
          setRequiredNetworkId(BASE_CHAIN_ID); // Set required network ID
          console.log(`[DEBUG ${DEBUG_ID}] Network mismatch for ${selectedCrypto}. Current: ${chainId}, Expected: ${BASE_CHAIN_ID}`);
          setProcessing(false);
          return;
        }

        let gasParams = {};
        
        // Create transaction object
        const tx: any = {
          chainId: BASE_CHAIN_ID,
          to: baseTreasuryAddress as `0x${string}`,
          value: parseEther(cryptoAmountToPay.toString()),
          data: toHex(ethers.toUtf8Bytes(`GAM Pre-Mint: ${tokensToReceive.toFixed(2)} tokens on Base`)),
          gasLimit: 50000, 
        };

      console.log(`[DEBUG ${DEBUG_ID}] Sending ETH_BASE tx via wagmi action to ${baseTreasuryAddress}:`, tx);
        transactionHash = await sendTransaction(wagmiAdapter.wagmiConfig, tx);
      } else if (selectedCrypto === 'SOL') {
        console.log(`[DEBUG ${DEBUG_ID}] Preparing SOL transaction...`);
        const solanaTreasuryAddress = SOLANA_TREASURY_ADDRESS || TREASURY_ADDRESS;

        // *** Simplified Solana Handling ***
        // We rely on the AppKit modal to handle connecting the correct Solana wallet 
        // and being on the correct network. Direct sending from here is complex and less user-friendly.
        // The user needs to have a Solana wallet connected via the AppKit modal.
        
        // Check if a Solana wallet *might* be connected (simple check, AppKit manages the real state)
        // We can't directly check the connected Solana network easily here without more context/hooks.
        // A better approach might involve getting the active adapter/wallet from AppKit hooks.
        
        // For now, guide the user.
        setError('Solana Payment: Please ensure your Solana wallet (e.g., Phantom, Solflare) is connected via the main wallet button and you are on the Solana network. Transaction sending via Solana wallets is handled through the wallet extension directly.');
        console.log(`[DEBUG ${DEBUG_ID}] Solana payment selected. User needs to initiate send from their wallet.`);
        setProcessing(false);
        // We cannot proceed with `onNext` here as the transaction isn't initiated from the dApp directly.
        // The user needs to manually send `estimatedCrypto` SOL to `solanaTreasuryAddress`.
        // This flow needs refinement for a better UX, perhaps involving backend verification or Solana Pay.
        return; // Stop execution, user needs to act manually
      } else if (selectedCrypto === 'USDC_BASE') {
        console.log(`[DEBUG ${DEBUG_ID}] Preparing USDC_BASE transaction...`);
        const baseTreasuryAddress = BASE_TREASURY_ADDRESS || TREASURY_ADDRESS;
        const contractAddress = cryptoStaticData[selectedCrypto].contractAddress;
        
        if (!contractAddress) {
          throw new Error(`Missing contract address for ${selectedCrypto}`);
        }
        
        // Verify chain ID
        if (chainId !== BASE_CHAIN_ID) {
          setError('Incorrect Network: Please switch to the Base network to complete this transaction.');
          setRequiredNetworkId(BASE_CHAIN_ID);
          console.log(`[DEBUG ${DEBUG_ID}] Network mismatch for ${selectedCrypto}. Current: ${chainId}, Expected: ${BASE_CHAIN_ID}`);
          setProcessing(false);
          return;
        }
        
        let gasParams = {}; // Ensure this is the only one for USDC_BASE block scope
        
        // Create ERC-20 token interface
        const erc20ABI = [ // Restore/ensure erc20ABI for USDC_BASE
          "function allowance(address owner, address spender) external view returns (uint256)",
          "function approve(address spender, uint256 amount) external returns (bool)",
          "function transfer(address to, uint256 amount) external returns (bool)",
          "function transferFrom(address from, address to, uint256 amount) external returns (bool)",
          "function decimals() external view returns (uint8)",
        ];
        
        // Get Web3Provider from ethers
        const provider = new ethers.BrowserProvider(window.ethereum as any);
        
        const signer = await provider.getSigner();
        const tokenContract = new ethers.Contract(contractAddress, erc20ABI, signer);
        
        // Get token decimals
        const decimals = await tokenContract.decimals();
        // Format the amount string to match token decimals before parsing
        const amountStr = ethers.FixedNumber.fromString(cryptoAmountToPay.toString()).round(Number(decimals)).toString();
        const amountInWei = ethers.parseUnits(amountStr, decimals);
        
        // Add approval step for USDC on Base
        // NO NEED FOR SAFE APPROVE HERE - Standard approve is fine for most tokens other than USDT
        console.log(`[DEBUG ${DEBUG_ID}] Approving USDC_BASE transfer for ${amountInWei.toString()}`);
        const approveTx = await tokenContract.approve(baseTreasuryAddress, amountInWei, { 
          gasLimit: 60000, 
        });
        const approveReceipt = await approveTx.wait();
        console.log(`[DEBUG ${DEBUG_ID}] USDC_BASE approval confirmed with hash:`, approveReceipt.hash);
        
        // Send the transaction to the Base treasury address
        console.log(`[DEBUG ${DEBUG_ID}] Transferring ${cryptoAmountToPay} USDC_BASE to ${baseTreasuryAddress}`);
        const transferTx = await tokenContract.transfer(baseTreasuryAddress, amountInWei, { 
          gasLimit: 100000, 
        });
        
        // Wait for transaction to be mined
        const receipt = await transferTx.wait();
        transactionHash = receipt.hash;
        console.log(`[DEBUG ${DEBUG_ID}] USDC_BASE transfer confirmed with hash:`, transactionHash);
      } else {
        // ERC-20 token transfer
        console.log(`[DEBUG ${DEBUG_ID}] Preparing ${selectedCrypto} ERC-20 transfer...`);
        const contractAddress = cryptoStaticData[selectedCrypto].contractAddress;
        
        if (!contractAddress) {
          throw new Error(`Missing contract address for ${selectedCrypto}`);
        }
        
        let gasParams = {}; // Ensure this is the only one for generic ERC20 block scope
        
        // Create ERC-20 token interface
        const erc20ABI = [ // Restore/ensure erc20ABI for generic ERC20
          "function allowance(address owner, address spender) external view returns (uint256)",
          "function approve(address spender, uint256 amount) external returns (bool)",
          "function transfer(address to, uint256 amount) external returns (bool)",
          "function transferFrom(address from, address to, uint256 amount) external returns (bool)",
          "function decimals() external view returns (uint8)",
        ];
        
        // Get Web3Provider from ethers
        const provider = new ethers.BrowserProvider(window.ethereum as any);
        
        const signer = await provider.getSigner();
        const tokenContract = new ethers.Contract(contractAddress, erc20ABI, signer);
        
        // Get token decimals
        const decimals = await tokenContract.decimals();
        // Format the amount string to match token decimals before parsing
        const amountStr = ethers.FixedNumber.fromString(cryptoAmountToPay.toString()).round(Number(decimals)).toString();
        const amountInWei = ethers.parseUnits(amountStr, decimals);
        
        // For USDT and USDC tokens, add an approval step first (with safe USDT pattern)
        if (selectedCrypto === 'USDT' || selectedCrypto === 'USDC') {
          console.log(`[DEBUG ${DEBUG_ID}] Checking current ${selectedCrypto} allowance for ${TREASURY_ADDRESS}`);
          const currentAllowance = await tokenContract.allowance(address, TREASURY_ADDRESS);
          console.log(`[DEBUG ${DEBUG_ID}] Current ${selectedCrypto} allowance: ${currentAllowance.toString()}`);

          // USDT requires resetting allowance to 0 before setting a new non-zero allowance
          // Apply this safety pattern for both USDT and USDC in this generic handler
          if (currentAllowance > BigInt(0) && amountInWei > BigInt(0)) {
            console.log(`[DEBUG ${DEBUG_ID}] Current allowance is non-zero. Resetting ${selectedCrypto} allowance to 0 first.`);
            const zeroApproveTx = await tokenContract.approve(TREASURY_ADDRESS, BigInt(0), { 
              gasLimit: 60000, 
            });
            const zeroApproveReceipt = await zeroApproveTx.wait();
            console.log(`[DEBUG ${DEBUG_ID}] ${selectedCrypto} allowance reset confirmed with hash:`, zeroApproveReceipt.hash);
          }
          
          // Only proceed with the actual approval if the amount is greater than 0
          if (amountInWei > BigInt(0)) {
              console.log(`[DEBUG ${DEBUG_ID}] Approving ${selectedCrypto} transfer for ${amountInWei.toString()}`);
              const approveTx = await tokenContract.approve(TREASURY_ADDRESS, amountInWei, { 
                gasLimit: 60000, 
              });
              const approveReceipt = await approveTx.wait();
              console.log(`[DEBUG ${DEBUG_ID}] ${selectedCrypto} approval confirmed with hash:`, approveReceipt.hash);
          } else {
              console.log(`[DEBUG ${DEBUG_ID}] Skipping ${selectedCrypto} approval as amount is 0.`);
          }
        }
        
        // Send the transaction directly to the treasury address
        console.log(`[DEBUG ${DEBUG_ID}] Transferring ${cryptoAmountToPay} ${selectedCrypto} to ${TREASURY_ADDRESS}`);
        // Pass gasParams to the transfer transaction
        const transferTx = await tokenContract.transfer(TREASURY_ADDRESS, amountInWei, { 
          gasLimit: 100000, 
        });
        
        // Wait for transaction to be mined
        const receipt = await transferTx.wait();
        transactionHash = receipt.hash;
        console.log(`[DEBUG ${DEBUG_ID}] ${selectedCrypto} transfer confirmed with hash:`, transactionHash);
      }
      
      // Check if transactionHash is valid (not undefined or zero hash)
      if (!transactionHash || (typeof transactionHash === 'string' && transactionHash === ZeroHash)) { 
          throw new Error('Transaction failed or was rejected by wallet.'); 
      }
      console.log(`[DEBUG ${DEBUG_ID}] Transaction successful, hash: ${transactionHash}`);
      
      // Record the purchase (using address from useAccount)
      console.log(`[DEBUG ${DEBUG_ID}] Recording purchase with email: ${emailAddress}`);
      
      // Determine network based on selected crypto
      // Use type assertion to avoid linter error on comparison
      const network = (selectedCrypto as CryptoCurrency) === 'MATIC' ? 'polygon' :
                      (selectedCrypto as CryptoCurrency) === 'SOL' ? 'solana' :
                      (selectedCrypto as CryptoCurrency) === 'ETH_BASE' ? 'base' :
                      'ethereum';
                      
      // Determine treasury address to use based on network
      // Use type assertion to avoid linter error on comparison
      const treasuryAddressToUse = (selectedCrypto as CryptoCurrency) === 'MATIC' ?
        (POLYGON_TREASURY_ADDRESS || TREASURY_ADDRESS) :
        (selectedCrypto as CryptoCurrency) === 'SOL' ?
        (SOLANA_TREASURY_ADDRESS || TREASURY_ADDRESS) :
        (selectedCrypto as CryptoCurrency) === 'ETH_BASE' ?
        (BASE_TREASURY_ADDRESS || TREASURY_ADDRESS) :
        TREASURY_ADDRESS;

      const recordResponse = await fetch('/api/presale/purchase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          walletAddress: address, 
          email: emailAddress, 
          amountPaid: cryptoAmountToPay,
          paymentCurrency: selectedCrypto, 
          transactionHash, 
          tokenAmount: tokensToReceive,
          treasuryAddress: treasuryAddressToUse, 
          premint: true,
          referralCode: referralCode || undefined,  // Include referral code if present
          amount_usd: usdAmount, // Use the explicitly tracked USD amount
          network, // Include network information
        }),
      });
      const recordData = await recordResponse.json();
      if (!recordData.success) throw new Error(recordData.message || 'Failed to record purchase');
      
      console.log(`[DEBUG ${DEBUG_ID}] Purchase successfully recorded`);
      onNext(transactionHash.toString(), tokensToReceive);

    } catch (err: any) {
      console.error(`[DEBUG ${DEBUG_ID}] Purchase error:`, err);
      
      // Sanitize error message for display more thoroughly
      let displayError = 'Transaction failed. Please try again.';
      if (err.message?.includes('User rejected the request') || 
          err.message?.includes('User denied transaction signature') ||
          err.message?.includes('MetaMask Tx Signature: User denied')) {
        displayError = 'Transaction canceled. You declined the transaction in your wallet.';
      } else if (err.message) {
        // Replace problematic characters
        displayError = err.message.replace(/&/g, '&amp;') // Must be first
                                 .replace(/</g, '&lt;')
                                 .replace(/>/g, '&gt;')
                                 .replace(/"/g, '&quot;');
      }

      // Keep price fetching error if it exists
      if (error?.includes('live price')) {
          setError(error + ' Additionally: ' + displayError);
       } else {
          setError(displayError);
       }
    } finally {
      setProcessing(false);
      const submitTime = performance.now() - submitStart;
      console.log(`[DEBUG ${DEBUG_ID}] Submission process completed in ${submitTime.toFixed(1)}ms`);
    }
  };

  // --- Render --- 
  console.log(`[DEBUG ${DEBUG_ID}] Rendering PurchaseStep UI for ${selectedCrypto} (render #${renderCount.current})`);
  
  return (
    <>
      <button onClick={onPrevious} className={styles.backButton} disabled={processing}>
        ← Back to Select Crypto
      </button>
      
      {/* New container for side-by-side price info */}
      <div className={styles.priceInfoContainer}>
        {/* Display selected crypto price info */}
        <div className={styles.selectedCryptoCard}>
             <Image src={cryptoStaticData[selectedCrypto].iconUrl} alt={selectedCrypto} width={32} height={32} />
             <div>
                 <p>Current Price:
                     {loadingPrice ? ' Loading...' : 
                      selectedCryptoPrice ? ` $${selectedCryptoPrice.toLocaleString()} USD` : ' Error fetching price'}
                 </p>
             </div>
         </div>

        {/* GAM Token Price Info */}
        <div className={styles.paymentDetails}>
          <div className={styles.detailRow} style={{ borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }}> {/* Remove internal styling */}
            <div className={styles.detailLabel}>GAM Token Price</div>
            <div className={styles.detailValue}>${TOKEN_PRICE} USD</div>
          </div>
        </div>
      </div>
      
      {/* Input Mode Toggle */}
      <div className={styles.inputToggle}>
        <button 
          className={`${styles.toggleButton} ${inputMode === 'crypto' ? styles.activeToggle : ''}`}
          onClick={() => inputMode !== 'crypto' && setInputMode('crypto')}
          disabled={processing}
        >
          Enter {cryptoStaticData[selectedCrypto]?.name || selectedCrypto} Amount
        </button>
        <button 
          className={`${styles.toggleButton} ${inputMode === 'gam' ? styles.activeToggle : ''}`}
          onClick={() => inputMode !== 'gam' && setInputMode('gam')}
          disabled={processing}
        >
          Enter GAM Amount
        </button>
        <button 
          className={`${styles.toggleButton} ${inputMode === 'usd' ? styles.activeToggle : ''}`}
          onClick={() => inputMode !== 'usd' && setInputMode('usd')}
          disabled={processing}
        >
          Enter USD Amount
        </button>
      </div>
      
      {/* Informational message for USDT/USDC/USDC_BASE approval */}
      {showApprovalWarning && (selectedCrypto === 'USDT' || selectedCrypto === 'USDC' || selectedCrypto === 'USDC_BASE') && (
        <div style={{
          position: 'relative', // Needed for absolute positioning of the close button
          background: 'rgba(54, 211, 0, 0.1)', // Green background
          border: '1px solid rgba(54, 211, 0, 0.3)', // Green border
          borderRadius: '8px',
          padding: '10px 12px',
          paddingRight: '35px', // Add padding to avoid text overlapping with close button
          marginTop: '12px',
          fontSize: '13px',
          color: '#ffffff', // Set default text color for the container to white
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
        }}>
          {/* Close Button */}
          <button 
            onClick={() => setShowApprovalWarning(false)}
            aria-label="Dismiss approval notice"
            style={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              background: 'transparent',
              border: 'none',
              color: '#ffffff',
              fontSize: '16px',
              lineHeight: '1',
              cursor: 'pointer',
              padding: '0',
              opacity: 0.7
            }}
            onMouseOver={(e) => (e.currentTarget.style.opacity = '1')}
            onMouseOut={(e) => (e.currentTarget.style.opacity = '0.7')}
          >
            &times;
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '6px' }}>
              <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-11v6h2v-6h-2zm0-4v2h2V7h-2z" fill="#36d300"/>
            </svg>
            <strong style={{ color: '#ffffff', fontSize: '13px' }}>Important:</strong>
          </div>
          <p style={{ margin: '0 0 4px 0', color: '#ffffff' }}>When paying with {selectedCrypto}, you&apos;ll need to approve two transactions:</p>
          <ol style={{margin: '0 0 0 20px', padding: 0, color: '#ffffff', fontSize: '12px'}}> {/* Ensure list items are also white */}
            <li>First approve the token spending (you may see a security warning from MetaMask - this is normal)</li>
            <li>Then confirm the actual payment transaction</li>
          </ol>
        </div>
      )}
      
      {/* Amount Input Form */}
      <form onSubmit={handleSubmit} style={{marginTop: '1.5rem'}}>
         <div className={styles.amountInput}> 
           <label>
               {inputMode === 'crypto' ? `Amount in ${cryptoStaticData[selectedCrypto]?.name || selectedCrypto}` : 
                inputMode === 'gam' ? 'Amount in GAM' : 'Amount in USD'}
           </label>
           <input
               type="text" 
               inputMode="decimal" 
               value={amount}
               onChange={handleAmountChange}
               placeholder="0.0"
               disabled={loadingPrice || processing} // Disable if price is loading
               required 
           />
         </div>
          
         {/* Conversion Result */} 
         {useMemo(() => { 
             const cryptoAmount = estimatedCrypto;
             const tokenAmount = estimatedTokens;
             const usdAmount = estimatedUsd;
             // Disable display if price isn't loaded yet
             if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0 || loadingPrice || !selectedCryptoPrice) return null;
             
             return (
                 <div className={styles.conversionResult}>
                     {inputMode === 'crypto' ? (
                         <p>You will receive approx: <strong>{tokenAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} GAM</strong> (${usdAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} USD)</p>
                     ) : inputMode === 'gam' ? (
                         <p>You will pay approx: <strong>{cryptoAmount.toLocaleString(undefined, { maximumFractionDigits: 6 })} {selectedCrypto}</strong> (${usdAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} USD)</p>
                     ) : (
                         <p>You will pay approx: <strong>{cryptoAmount.toLocaleString(undefined, { maximumFractionDigits: 6 })} {selectedCrypto}</strong> and receive <strong>{tokenAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} GAM</strong></p>
                     )}
                 </div>
             );
         }, [amount, inputMode, estimatedTokens, estimatedCrypto, estimatedUsd, loadingPrice, selectedCryptoPrice, selectedCrypto])}

        {error && (
            <div className={styles.errorContainer} style={{ marginBottom: '1rem' }}>
              {/* Display error directly */}
              <div className={styles.errorBox}>{error}</div> 
              {/* Conditionally render the switch network button */} 
              {requiredNetworkId && (
                 <button
                    type="button" // Important: Prevent form submission
                    onClick={handleSwitchNetwork}
                    className={`${styles.button} ${styles.switchNetworkButton}`}
                    style={{ marginTop: '10px', width: '100%' }}
                    disabled={processing}
                 >
                    {/* Dynamically display network name */}
                    {processing ? 'Switching...' : 
                      `Switch to ${requiredNetworkId === ETHEREUM_CHAIN_ID ? 'Ethereum' : 
                                     requiredNetworkId === POLYGON_CHAIN_ID ? 'Polygon' : 
                                     requiredNetworkId === BASE_CHAIN_ID ? 'Base' : 'Required'} Network`}
                 </button>
               )}
            </div>
         )}
        
        <button 
          type="submit" 
          className={styles.purchaseButton} 
          // Disable submit if network switch is required
          disabled={!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0 || processing || loadingPrice || !selectedCryptoPrice || !!requiredNetworkId || !cryptoStaticData[selectedCrypto] }
        >
          {processing ? (
              <><span className={styles.spinner} style={{height: '1rem', width: '1rem', marginRight: '0.5rem'}}></span> Processing...</>
          ) : loadingPrice ? (
              <><span className={styles.spinner} style={{height: '1rem', width: '1rem', marginRight: '0.5rem'}}></span> Loading Price...</>
          ) : (
              'Complete Purchase'
          )}
        </button>

        {/* SAFT Agreement Button */}
        <button 
          type="button" // Important: Not a submit button for the main form
          onClick={() => setIsSaftModalOpen(true)}
          className={`${styles.button} ${styles.saftAgreementButton}`}
          disabled={processing || loadingPrice} // Disable if main form is busy
          style={{ marginTop: '1rem', width: '100%' }} // Basic styling, adjust as needed
        >
          Request SAFT Agreement
        </button>

      </form>

      <SaftAgreementModal 
        isOpen={isSaftModalOpen}
        onClose={() => setIsSaftModalOpen(false)}
        email={emailAddress || undefined} // Prefill email if available
        // name={userName || undefined} // Prefill name if you have it
      />
    </>
  );
};

// Wrap the component with React.memo for export
export const PurchaseStep = React.memo(PurchaseStepComponent);

// Optional: Add display name for better debugging
PurchaseStep.displayName = 'PurchaseStep';